<template>
  <div class="wrapper dashboard">
    <side-bar type="sidebar" />

    <div
      v-if="isFraudFamilyCustomer"
      class="fraud-error-message"
    >
      <p>
        Please give us a call at 1-855-464-6872 to finalize your registration.<br>
        After that is done, please click the refresh button below to gain access to your account!
      </p>
      <button @click="refreshPage">
        <i class="fa fa-refresh" />
      </button>
    </div>

    <div
      v-if="isPartnerDisabled"
      class="fraud-error-message"
    >
      <p>
        Your account was disabled and please contact support team to enable.<br>
        After that is done, please click the refresh button below to gain access to your account!
      </p>
      <button @click="refreshPage">
        <i class="fa fa-refresh" />
      </button>
    </div>

    <div
      v-if="!isFraudFamilyCustomer && !isDeactivatedFamilyCustomer"
      class="main-panel"
      :class="{
        left: status.isShowLeftSidebar,
        right: status.isShowRightSidebar,
        'dynamic-panel': status.isRightPanelVisible,
        'family-dashboard': isFamilyDashboard,
        'partner-dashboard': isPartnerDashboard,
      }"
    >
      <top-navbar
        @toggle-left-sidebar="toggleLeftSidebar"
        @toggle-right-sidebar="toggleRightSidebar"
      />

      <dashboard-content v-if="isContentVisible" />
    </div>

    <right-panel
      v-if="status.isRightPanelVisible"
      type="sidebar"
    />

    <customer-notes-dialog />
    <gourmet-cross-sale-provider />
    <reactivate-account-dialog
      :is-show-dialog="status.isShowReactivateDialog"
      :plan-info="planInfo"
      :plan-list="planList"
      @cancel="cancelReactivateAccount"
      @submit="reactivateAccount"
    />
  </div>
</template>

<script lang="ts">
import { ref, Ref, reactive, toRefs, computed, watch, onMounted, getCurrentInstance, h, defineAsyncComponent, Fragment } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElLoading, ElNotification } from 'element-plus';
import { graphql } from '@/gql';
import MomentTimezone from 'moment-timezone';
import 'cleave.js/dist/addons/cleave-phone.us';
import events from '@/events';

import util from '@/Utils/util';
import { meOperator } from '@/services/operators/viewModel';
import { useProfile } from '@/use/profile';
import { useDashboard } from '@/use/app';
import { useSwalDialog } from '@/use/swalDialog';
import { usePartner } from '@/use/partner';
import { loadPaymentTiers } from '@/use/paymentTiers';
import { useRideTypes } from '@/use/rideTypes';
import { useCustomer, isSimpleRideOrderingTest } from '@/use/customer';
import { useGlobal } from '@/use/global';
import { useTwilio } from '@/use/twilio';
import { useSound } from '@/use/sound';
import { subscribeExecute, heartbeatService } from '@/Utils/graphqlAPI';
import { Announcement } from '@/models/announcement';
import { CallerPlan } from '@/models/callerPlan';
import { CustomizedPlan } from '@/models/plan';
import { CallerAddress, CallerInput } from '@/models/caller';
import { CustomLocationType } from '@/models/address';
import { PushNotificationCode } from '@/models/app';

import { fetchOperator } from '@/services/operators/fetchOperator';
import { callerPlanService } from '@/services/callerPlan';
import { announcementService } from '@/services/announcement';
import { planService } from '@/services/plan';
import { callerService } from '@/services/caller';

import TopNavbar from './TopNavbar.vue';
import DashboardContent from './Content.vue';
import CustomerNotesDialog from './Dialogs/CustomerNotes.vue';
import ReactivateAccountDialog from './Dialogs/ReactivateAccount.vue';
import { fetchMeOperatorFirstRideOfferInventory } from '@/services/operator-first-ride-offer-inventory/viewModel';
import { log } from '@/Utils/logger';

const GourmetCrossSaleProvider = defineAsyncComponent(() => import('./GourmetCrossSaleProvider.vue') as any);

export default {
  name: 'DashboardLayout',

  components: {
    TopNavbar,
    DashboardContent,
    CustomerNotesDialog,
    GourmetCrossSaleProvider,
    ReactivateAccountDialog,
  },

  setup: () => {
    const route = useRoute();
    const router = useRouter();

    const instance: any = getCurrentInstance();

    const { currentUser, userRoles, isFraudFamilyCustomer, isDeactivatedFamilyCustomer, fetchCurrentUser } = useProfile();
    const { isCustomerUser, isFamilyUser, isPartnerAdmin, isAffiliateUser, isManagementRole } = toRefs(userRoles);

    const { dashboardCategory, isFamilyDashboard, isPartnerDashboard } = useDashboard();

    const { showSuccessPopupDialog, showErrorPopupDialog, showWarningPopupDialog } = useSwalDialog();

    const { partnerId } = usePartner();
    const { fetchLocalTaxis } = useRideTypes();
    const { customerInfo, customerStatus, setCustomerInfo, setCustomerActivePlan, setCustomerPhoneNumber } = useCustomer();
    const { isMembershipSuspended } = toRefs(customerStatus);
    const {
      smsBoardState,
      setNewMessage,
      setActiveAnnouncements,
      setIncomingCallDetail,
      getTeamLeads,
      getSupervisors,
      getAllOperators,
      getOutboundPhoneNumbers,
    } = useGlobal();
    const { setupTwilioConfiguration } = useTwilio();
    const newNotificationAudio = useSound('/static/notification.wav');
    const newMessageAudio = useSound('/static/bubblepop.wav');

    const status = reactive({
      isRightPanelVisible: false,
      isSetupGoogle: false,
      isShowLeftSidebar: false,
      isShowRightSidebar: false,
      isShowReactivateDialog: false,
    });

    const newMessageObserver: Ref<any> = ref(null);
    const newNotificationObserver: Ref<any> = ref(null);
    const heartbeatInterval: Ref<any> = ref(null);
    const planInfo: Ref<CallerPlan | null> = ref(null);
    const planList: Ref<CustomizedPlan[]> = ref([]);

    const isMainDashboard = ref(false);

    const isContentVisible = computed(() => {
      if (!currentUser.value || !currentUser.value.id) {
        log.debug({
          func: 'DashboardLayout.isContentVisible',
          currentUser: currentUser.value,
          status: status.isSetupGoogle
        }, 'isContentVisible is false');
        return false;
      }

      if ((isFamilyUser.value || isCustomerUser.value) && !customerInfo.value.id) {
        return false;
      }

      return true;
    });

    const isPartnerDisabled = computed(() => {
      if (!isPartnerDashboard.value) {
        return false;
      }

      if (!isPartnerAdmin.value) {
        return false;
      }

      return !partnerId.value;
    });

    const fetchMe = async () => {
      const loading = ElLoading.service({
        lock: true,
        text: 'Loading Information...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      try {
        const response = (await fetchCurrentUser())!; // TODO(cem): ENG-7105
        const isCustomerOrFamilyMember = response.roles!.includes('customer') || response.roles!.includes('family'); // TODO(cem): ENG-7105
        const isAffiliate = response.roles!.includes('affiliate'); // TODO(cem): ENG-7105

        // if logged operator is not active, please do log out
        if (!(response.isActive || isCustomerOrFamilyMember || isAffiliate)) {
          logout();

          return;
        }

        if (!isCustomerOrFamilyMember) {
          meOperator.value = await fetchOperator(response.id);
          if (meOperator.value) {
            await fetchMeOperatorFirstRideOfferInventory(response.id);
          }
        }

        // if logged user is family or customer,
        // set the phone number of family dashboard with
        // current user's phone number
        if (isFamilyUser.value || isCustomerUser.value) {
          setCustomerPhoneNumber(response.phoneNumber!); // TODO(cem): ENG-7105
          setCustomerActivePlan(null);

          const callerInfo = await loadCustomerInfo(response.phoneNumber!); // TODO(cem): ENG-7105

          if (!callerInfo) {
            return logout();
          }

          callerInfo.customAddresses = await getCustomAddresses(callerInfo.id);

          setCustomerInfo(callerInfo);

          await setActiveCallerSubscription(response.phoneNumber!); // TODO(cem): ENG-7105

          if (!response.isActive) {
            const planListResponse = await planService.getAvailablePlansForCaller({
              callerId: callerInfo.id,
            });
            planList.value = planListResponse
              .map((plan: CustomizedPlan) => {
                let label = `${plan.name} ${plan.price || ''}`;

                if (plan.isTrial) {
                  label = `${plan.name} Trial (${plan.trialPrice || ''} instead of ${plan.price || ''})`;
                }

                return {
                  ...plan,
                  key: plan.value || '',
                  label,
                };
              })
              .filter((plan: CustomizedPlan) => plan.type !== 'free' && plan.value);

            status.isShowReactivateDialog = true;
          }
        }

        changeDashboardLayout();

        // create graphql subscription for new message
        createNewMessageSubscription();

        // create graphql subscription for new notification
        createNotificationSubscription();

        // start heartbeat
        startHeartbeat();

        showPopupToUpdatePassword();
      } catch (error) {
        console.log(error);
      } finally {
        loading.close();
      }
    };

    const initGooglePlaces = async () => {
      try {
        await instance.ctx.$gmapApiPromiseLazy();
        status.isSetupGoogle = true;
      } catch (error) {
        console.log(error);
        log.error({
          func: 'DashboardLayout.initGooglePlaces',
          error: error,
        }, 'Google Maps init failed');
      }
    };

    const loadCustomerInfo = async (phoneNumber: string) => {
      try {
        const caller = await callerService.fetchCustomerInfo({
          phoneNumber,
        });

        if (caller.dob) {
          caller.dob = MomentTimezone(caller.dob).toDate();
        }

        return caller;
      } catch (error) {
        logout();
      }
    };

    const getCustomAddresses = async (callerId: string) => {
      try {
        const response = await callerService.fetchCustomerAddress({
          callerId,
        });

        const callerAddresses = response.rows.map((customLocation: CallerAddress, index: number) => ({
          ...customLocation,
          id: customLocation.id.toString(),
          index,
        }) as CustomLocationType);

        return callerAddresses;
      } catch (error) {
        return [];
      }
    };

    const setActiveCallerSubscription = async (phoneNumber: string) => {
      try {
        planInfo.value = await callerPlanService.fetchActiveCallerSubscription({ phoneNumber });
        /*
          Note that an account can be inactive but still have an active plan (when a user disables their account, we let the paid plans continue to run until they expire)
        */

        if (!planInfo.value) {
          return;
        }

        planInfo.value.planEndAt = util.formatDateTime(planInfo.value.expectedPeriodEndAt, 'YYYY-MM-DD');

        setCustomerActivePlan(planInfo.value);

        if (isMembershipSuspended.value) {
          const errorMessage = `Your membership was suspended${planInfo.value.timePlanSuspendPeriodStartDate ? ` from ${planInfo.value.timePlanSuspendPeriodStartDate}` : ''
            } until ${planInfo.value.timePlanSuspendPeriodEndDate
            }. Please give support team a call at 1-855-464-6872 before ordering any ride, gourmet, groceries, pharmacy delivery or home services.`;

          showErrorPopupDialog({
            text: errorMessage,
            title: 'Membership Suspended',
          });
        }
      } catch (error) {
        return;
      }
    };

    // force refresh the page
    const refreshPage = () => {
      location.reload();
    };

    const toggleLeftSidebar = () => {
      status.isShowRightSidebar = false;
      status.isShowLeftSidebar = !status.isShowLeftSidebar;
    };

    const toggleRightSidebar = () => {
      status.isShowLeftSidebar = false;
      status.isShowRightSidebar = !status.isShowRightSidebar;
    };

    // if current user is family or customer, no need to show right panel
    // if current user is not family or customer, no need to show crip
    const changeDashboardLayout = () => {
      if (!currentUser.value?.id) {
        return;
      }

      if (!isFamilyUser.value && !isCustomerUser.value && !isAffiliateUser.value) {
        status.isRightPanelVisible = true;
        document.body.classList.add('no-crisp');
      } else {
        status.isRightPanelVisible = false;
        document.body.classList.remove('no-crisp');
      }
    };

    // create graphql subscription for new message
    // if there is new message, it will be stored into vuex
    const createNewMessageSubscription = () => {
      const mapObserver = {
        next: (msg: any) => setNewMessage(msg.data.newMessage),
        error: (err: any) => console.log(err),
      };

      if (!newMessageObserver.value) {
        newMessageObserver.value = subscribeExecute({
          query: graphql(`
            subscription NewMessage($operatorId: String!) {
              newMessage(operatorId: $operatorId) {
                id
                chatRoomId
                fromNumber
                message
                isRead
              }
            }
          `),
          variables: {
            operatorId: currentUser.value.id,
          },
        });
        newMessageObserver.value.subscribe(mapObserver);
      }
    };

    // create graphql subscription for new notification
    // if there is new notification, it will be stored into vuex
    const createNotificationSubscription = () => {
      const mapObserver = {
        next: (msg: any) => processNotification(msg.data.pushNotification),
        error: (err: any) => console.log(err),
      };

      if (!newNotificationObserver.value) {
        newNotificationObserver.value = subscribeExecute({
          query: graphql(`
            subscription PushNotification($operatorId: String!) {
              pushNotification(operatorId: $operatorId) {
                subject
                message
                code
                data
              }
            }
          `),
          variables: {
            operatorId: currentUser.value.id,
          },
        });
        newNotificationObserver.value.subscribe(mapObserver);
      }
    };

    const startHeartbeat = () => {
      const isEnabled = import.meta.env.VITE_HEARTBEAT_ENABLED === 'true';
      const defaultInterval = parseInt(import.meta.env.VITE_HEARTBEAT_INTERVAL || '20000');

      if (!isEnabled) {
        return;
      }

      // Only start heartbeat for operators (GoGo employees), not for family/customer users
      if (!userRoles.isGoGoEmployee) {
        return;
      }

      if (!heartbeatInterval.value) {
        heartbeatInterval.value = heartbeatService.startHeartbeat(defaultInterval);
      }
    };

    const reactivateAccount = async (planName: string) => {
      status.isShowReactivateDialog = false;
      const loading = ElLoading.service({
        lock: true,
        text: 'Reactivating account...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      const data: CallerInput = {
        isActive: true,
      };

      // reactivate with plan only if there's no current active plan
      if (planName && !planInfo.value?.planName) {
        data.planName = planName;
      }

      try {
        await callerService.updateCaller({
          callerId: currentUser.value.id,
          data,
        });

        loading.close();
        await showSuccessPopupDialog({
          text: 'Your account has been activated successfully.',
        });

        location.reload();
      } catch (error) {
        loading.close();

        const errorText = util.getGraphqlError(error, false);
        await showErrorPopupDialog({
          text: errorText,
        });

        status.isShowReactivateDialog = true;
      }
    };

    const cancelReactivateAccount = () => {
      status.isShowReactivateDialog = false;
      logout();
    };

    const logout = () => {
      localStorage.clear();
      window.location.href = '/login';
    };

    // if there is new notification and it is for incoming call, play audio and show notify
    const processNotification = (notification: { code: string; message: string; subject: string; data: any }) => {
      if (!notification) {
        return;
      }

      if (
        notification.code === PushNotificationCode.ClientStripeError
        || notification.code === PushNotificationCode.ClientCardAdded
        || notification.code === PushNotificationCode.ClientCardLinkAccessed
      ) {
        events.emit('set-client-stripe-status', notification);
        return;
      }

      if (notification.code === PushNotificationCode.FollowUpCallStarted) {
        const followUpRequestId = Number(notification.data?.followUpRequestId);
        if (Number.isFinite(followUpRequestId)) {
          events.emit('follow-up-call-started', followUpRequestId);
        }
        return;
      }

      let durationSeconds = 10;

      if (notification.code === PushNotificationCode.IncomingCall) {
        durationSeconds = 30;
        newNotificationAudio.play();
        setIncomingCallDetail(notification.subject);
      }

      if (notification.code === PushNotificationCode.ReservedOutboundCall) {
        events.emit('reserved-outbound-call', notification.data);
      }
      if (notification.code === PushNotificationCode.ReservedOutboundCallCancelled) {
        events.emit('reserved-outbound-call-cancelled');
      }

      // if notification has redirection link, we should create VNode.
      // ref: https://element-plus.org/en-US/component/notification.html#options
      let message = notification.data?.linkUrl
        ? h(Fragment, [
            h('div', {
              innerHTML: `${notification.message}<br />`,
            }),
            h('a', {
              href: '#',
              innerHTML: notification.data.linkText || notification.data.linkUrl,
              onClick: () => {
                router.push(notification.data.linkUrl);
              },
            }),
          ])
        : notification.message;

      if (notification.code === PushNotificationCode.WelcomeTextInbox) {
        message = h(Fragment, [
          h('div', {
              innerHTML: `<b>New Welcome Inbox Message</b><br />`,
            }),
          message
        ])
      }

      if (notification.code === PushNotificationCode.IncomingCall && notification.data?.utmTerm) {
        message = h(Fragment, [
          typeof message === 'string' ? h('div', message) : message,
          h('div', `Looking for: ${notification.data.utmTerm}`),
        ]);
      }

      const isBigNotification = notification.code === PushNotificationCode.DriverCanceled || notification.code === PushNotificationCode.MaxDriverCancels;
      ElNotification({
        message,
        type: 'success',
        title: `${notification.subject}`,
        duration: durationSeconds * 1000,
        customClass: isBigNotification ? 'big-notification' : 'medium-notification',
      });
    };

    const getActiveAnnouncements = async () => {
      const response = await announcementService.fetchAnnouncements({
        isActive: true,
        pagination: {
          offset: 0,
          limit: 100,
        },
      });

      const activeAnnouncements = response.rows
        .map((item: Announcement) => item)
        .filter((item: Announcement) => {
          if (!item.repeatedDate) {
            return true;
          }

          const currentDate = MomentTimezone.tz(item.timeZone).format('MM-DD');
          return item.repeatedDate === currentDate;
        });

      setActiveAnnouncements(activeAnnouncements);
    };

    const getTeamLeadsAndSups = async () => {
      await getTeamLeads();
      await getSupervisors();

      if (isManagementRole.value) {
        await getAllOperators();
      }
    };

    // if current tab is Main dashboard, it is okay
    // if not, make callback to check the changes of tab status
    const checkMainDashboard = () => {
      const targetNode = document.getElementById('dashboardLevel');
      if (targetNode && targetNode.classList.value === 'main') {
        isMainDashboard.value = true;
        return;
      }

      const config = { attributes: true, childList: true, subtree: true };
      const callback = (mutationsList: any) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'attributes' && mutation.target.className === 'main') {
            isMainDashboard.value = true;
          }
        }
      };

      const observer = new MutationObserver(callback);
      if (targetNode) {
        observer.observe(targetNode, config);
      }
    };

    const showPopupToUpdatePassword = async () => {
      if (util.isDevMode) {
        return;
      }

      if (!currentUser.value?.id || !currentUser.value?.isNeedToUpdatePassword) {
        return;
      }

      if (isFamilyUser.value || isCustomerUser.value) {
        return;
      }

      await showWarningPopupDialog({
        text: 'Your password has expired, please create a new unique password to secure your dashboard account.',
      });

      router.push({
        path: '/operator/account',
      });
    };

    watch(dashboardCategory, changeDashboardLayout, {
      immediate: true,
    });

    // if there is new message, play audio and show notify
    watch(
      () => smsBoardState.newMessage,
      (value: any) => {
        if (!value) {
          return;
        }

        newMessageAudio.play();
        ElNotification({
          message: value.message,
          type: 'success',
          title: `New Message - ${value.fromNumber}`,
          duration: 10 * 1000,
          onClick: () => {
            if (!route.path.includes('sms-board')) {
              router.push({
                path: '/operator/sms-board',
                query: {
                  phoneNumber: value.fromNumber,
                },
              });
            }
          },
        });
      },
    );

    watch(isMainDashboard, (value: boolean) => {
      if (!value) {
        return;
      }

      setupTwilioConfiguration();
    });

    const redirectSimpleRideOrdering = () => {
      if (!isFamilyDashboard.value) return;

      const abTest = customerInfo.value?.associatedAbTests?.find((ab: any) => ab.abTestName === 'simple_ride_ordering');
      isSimpleRideOrderingTest.value = abTest?.abTestGroupType === 'test';

      if (isSimpleRideOrderingTest.value) {
        const routeName = route.name as string;
        if (routeName === 'FamilyRideOrdering') {
          router.replace({ name: 'FamilyBookRide' });
        }
      }
    };

    onMounted(async () => {
      await initGooglePlaces();
      await fetchMe();

      checkMainDashboard();

      redirectSimpleRideOrdering();

      if (!isFamilyDashboard.value && !isPartnerDashboard.value) {
        getActiveAnnouncements();
        getTeamLeadsAndSups();
        getOutboundPhoneNumbers();
      }

      fetchLocalTaxis();
      loadPaymentTiers();

      util.checkEnvironemtVariables();
    });

    return {
      instance,
      isFamilyDashboard,
      isPartnerDashboard,
      status,
      isContentVisible,
      isFraudFamilyCustomer,
      isDeactivatedFamilyCustomer,
      isPartnerDisabled,
      planList,
      planInfo,

      reactivateAccount,
      cancelReactivateAccount,
      refreshPage,
      toggleLeftSidebar,
      toggleRightSidebar,
    };
  },
};
</script>

<style lang="scss">
.dashboard {
  background: #f4f3ef;

  .family-dashboard {
    .card {
      max-width: 960px;
    }
  }

  @media (max-width: 992px) {
    .content {
      padding-left: 5px !important;
      padding-right: 5px !important;
    }
  }

  @media (max-width: 992px) {

    .main-panel,
    .navbar .container,
    .wrapper-full-page {
      // transform: translateZ(0);
      transition: all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1);
      left: 0;
    }
  }

  .sidebar .sidebar-wrapper {
    width: 100% !important;
    padding-bottom: 30px;
    overflow-x: hidden;
    height: calc(100vh - 70px);
  }

  @media (max-width: 992px) {
    .dynamic-panel {
      width: 100%;
    }
.sidebar .sidebar-wrapper li.active>a:not([data-toggle='collapse']):before,
.off-canvas-sidebar .sidebar-wrapper li.active>a:not([data-toggle='collapse']):before,
.sidebar .sidebar-wrapper li.active>a:not([data-toggle='collapse']):after,
.off-canvas-sidebar .sidebar-wrapper li.active>a:not([data-toggle='collapse']):after {
      border-left: 0;
      border-right: 17px solid #cfcfca;
      right: 0;
    }
  }

  .navbar-toggle {
    float: left;
    padding-left: 10px;
  }

  .fraud-error-message {
    position: fixed;
    top: 0;
    left: 260px;
    z-index: 9999;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    font-size: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    flex-direction: column;

    button {
      width: 50px !important;
      height: 50px;
      border-radius: 50%;
      font-size: 28px;
      padding-left: 0;
      padding-right: 0;
      outline: none;
      color: #b95b5b;
      min-width: unset;
    }
  }
}

.sidebar .logo {
  align-items: center;
  padding: 7px 0.7rem 20px !important;
}

.sidebar .logo .logo-normal {
  margin-right: 20px;
}

.sidebar .logo .simple-text {
  color: #fff !important;
}

.sidebar .logo div {
  line-height: 1;
  margin: 5px 0 0;
  font-size: 14px;
  width: 100%;
  color: #fff;
  text-align: center;
}

.sidebar .logo .gogo-label {
  text-align: center;
  color: #debd0e;
  font-weight: bolder;
  font-size: 13px;
}

.sidebar-mini .dashboard .sidebar {
  @media (min-width: 991px) {
    z-index: 9999;
  }
}
.sidebar .nav li.active>a,
.sidebar .nav li.active>a>i,
.off-canvas-sidebar .nav li.active>a,
.off-canvas-sidebar .nav li.active>a>i {
  color: #fff;
}

.pac-container {
  z-index: 3000 !important;
}

.el-notification.big-notification {
  width: 600px;
  padding: 50px 30px;
}

.el-notification.medium-notification {
  width: 380px;
}
</style>
